export interface MoneyFormatOptions {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  compact?: boolean;
  currencyDisplay?: Intl.NumberFormatOptions["currencyDisplay"];
  signDisplay?: Intl.NumberFormatOptions["signDisplay"];
  fallback?: string;
}

const DEFAULT_FALLBACK = "—";
const DEFAULT_LOCALE = "en-NP";
const DEFAULT_CURRENCY = "NPR";

/**
 * Display symbols shown before the amount in the default (non-Intl) format
 * path — e.g. "रू 959", "$ 100", "₹ 500". Cleaner than the ISO code and what
 * merchants recognise at a glance. Falls back to the raw ISO code for any
 * currency not listed, so new backend currencies still render legibly.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  NPR: "रू",
  USD: "$",
  INR: "₹",
};

/** Symbol for a currency code, or the code itself when we have no symbol. */
export function getCurrencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[code] ?? code;
}

const _cache = new Map<string, Intl.NumberFormat>();

function _cacheKey(opts: Intl.NumberFormatOptions, locale: string): string {
  return `${locale}|${JSON.stringify(opts)}`;
}

function _getFormatter(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = _cacheKey(opts, locale);
  let fmt = _cache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, opts);
    _cache.set(key, fmt);
  }
  return fmt;
}

export function toNumberSafe(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  return isNaN(n) || !isFinite(n) ? null : n;
}

export function formatMoney(amount: unknown, options: MoneyFormatOptions = {}): string {
  const {
    currency = DEFAULT_CURRENCY,
    locale = DEFAULT_LOCALE,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    compact = false,
    currencyDisplay,
    signDisplay,
    fallback = DEFAULT_FALLBACK,
  } = options;

  const n = toNumberSafe(amount);
  if (n === null) return fallback;

  const intlOpts: Intl.NumberFormatOptions = {
    style: "decimal",
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: true,
    ...(compact ? { notation: "compact", compactDisplay: "short" } : {}),
    ...(signDisplay ? { signDisplay } : {}),
  };

  const formatted = _getFormatter(locale, intlOpts).format(n);

  if (currencyDisplay === "none" || currencyDisplay === undefined) {
    return `${getCurrencySymbol(currency)} ${formatted}`;
  }

  const currencyIntlOpts: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    currencyDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: true,
    ...(compact ? { notation: "compact", compactDisplay: "short" } : {}),
    ...(signDisplay ? { signDisplay } : {}),
  };
  return _getFormatter(locale, currencyIntlOpts).format(n);
}

/** Threshold above which compact/abbreviated notation is used. */
export const COMPACT_THRESHOLD = 100_000;

/**
 * Format a money amount with compact notation (K/M) — but only when the
 * value is ≥ 100,000. Below that threshold the full formatted number is
 * returned so small amounts like रू 45,300 never read as रू 45.3K.
 */
export function formatMoneyCompact(
  amount: unknown,
  options: Omit<MoneyFormatOptions, "compact"> = {},
): string {
  const n = toNumberSafe(amount);
  const useCompact = n !== null && Math.abs(n) >= COMPACT_THRESHOLD;
  return formatMoney(amount, {
    minimumFractionDigits: useCompact ? 0 : 2,
    maximumFractionDigits: useCompact ? 1 : 2,
    ...options,
    compact: useCompact,
  });
}
